import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { BrandingProvider } from "./context/BrandingContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import PublicSignPage from "./pages/PublicSignPage";
import SignatureConfirmationPage from "./pages/SignatureConfirmationPage";
import BookingConfirmationPage from "./pages/BookingConfirmationPage";
import TrackBookingPage from "./pages/TrackBookingPage";
import ConstitutionPage from "./pages/ConstitutionPage";
import AssociationsPage from "./pages/AssociationsPage";
import AssociationDetailPage from "./pages/AssociationDetailPage";
import CalendarPage from "./pages/CalendarPage";
import CalendarGuidePage from "./pages/CalendarGuidePage";
import EventViewPage from "./pages/EventViewPage";
import TentReservationPage from "./pages/TentReservationPage";
import SponsorApplicationPage from "./pages/SponsorApplicationPage";
import VendorDirectoryPage from "./pages/VendorDirectoryPage";
import ReservationTrackingPage from "./pages/ReservationTrackingPage";
import VideoRecorderPage from "./pages/VideoRecorderPage";
import AfadaVideoPage from "./pages/AfadaVideoPage";
import GalleryPage from "./pages/GalleryPage";
import ActivitiesPage from "./pages/ActivitiesPage";
import AssociationManagePage from "./pages/AssociationManagePage";
import AdminDashboard from "./pages/AdminDashboard";
import ProfilePage from "./pages/ProfilePage";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && user.role !== 'admin' && user.role !== 'president') {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Association slugs for direct URL routing
const ASSOCIATION_SLUGS = [
  'ngoketunjia', 'meca', 'menfa', 'bonbani', 'fako', 'kom',
  'lecda', 'seamoca', 'macuda', 'momo', 'kmf', 'oroko', 'bafut',
];

function App() {
  return (
    <LanguageProvider>
      <BrandingProvider>
        <AuthProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sign" element={<PublicSignPage />} />
            <Route path="/sign/confirmation" element={<SignatureConfirmationPage />} />
            <Route path="/associations" element={<AssociationsPage />} />
            
            {/* Legacy route - keep for backward compatibility */}
            <Route path="/associations/:slug" element={<AssociationDetailPage />} />
            
            {/* Direct slug routes - /ngoketunjia, /momo, /kmf, etc. */}
            {ASSOCIATION_SLUGS.map(slug => (
              <Route key={slug} path={`/${slug}`} element={<AssociationDetailPage />} />
            ))}
            
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/calendar/guide" element={<CalendarGuidePage />} />
            <Route path="/calendar/confirmation" element={<BookingConfirmationPage />} />
            <Route path="/events/:eventId" element={<EventViewPage />} />
            <Route path="/events/:eventId/reserve-tent" element={<TentReservationPage />} />
            <Route path="/events/:eventId/sponsor" element={<SponsorApplicationPage />} />
            <Route path="/events/:eventId/vendors" element={<VendorDirectoryPage />} />
            <Route path="/reserve/track/:confirmationNumber" element={<ReservationTrackingPage />} />
            <Route path="/reserve/track" element={<ReservationTrackingPage />} />
            <Route path="/record-invitation" element={<VideoRecorderPage />} />
            <Route path="/afada-convention" element={<AfadaVideoPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/track-booking" element={<TrackBookingPage />} />
            <Route path="/constitution" element={<ConstitutionPage />} />
            
            <Route path="/activities" element={
              <ProtectedRoute>
                <ActivitiesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            
            <Route path="/manage-associations" element={
              <ProtectedRoute adminOnly>
                <AssociationManagePage />
              </ProtectedRoute>
            } />
            
            <Route path="/admin/*" element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } />
          </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrandingProvider>
    </LanguageProvider>
  );
}

export default App;
